import React, { useEffect, useMemo, useState } from "react";
import CropFreeIcon from "@material-ui/icons/CropFree";
import FastfoodIcon from "@material-ui/icons/Fastfood";
import { Button as MuiButton, CircularProgress, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import Button from "../../../components/common/PL/Button/Button";
import { ReservationGuestCard } from "../components/ReservationGuestCard/ReservationGuestCard";
import { ReservationResultNotice } from "../components/ReservationResultNotice/ReservationResultNotice";
import { ReservationResultStatus } from "../components/ReservationResultStatus/ReservationResultStatus";
import { ReservationsHeader } from "../components/ReservationsHeader/ReservationsHeader";
import { ReservationActionModal } from "../components/ReservationActionModal/ReservationActionModal";
import { goToReservationScan, goToReservationsHome } from "../utils/reservationRoutes";
import {
  alternativeActionResult,
  RESERVATION_ACTION_EVENT_TYPE,
  RESERVATION_EVENT_RESULTS,
} from "./reservationResults";
import { SpaceEventAction, SpaceEventType } from "../../../passapp-sdk/types/spaceTypes";
import { buildReservationResult } from "../utils/spaceEventMappers";
import { clearLastSpaceScan, getLastSpaceScan } from "../store/reservationScanStore";
import useSpaceScan from "../hooks/useSpaceScan";
import useStyles from "../ReservationResult/ReservationResultStyles";

const ReservationResult = () => {
  const classes = useStyles();
  const history = useHistory();
  const { registerDecision } = useSpaceScan();
  const [scan] = useState(getLastSpaceScan);
  const [registeredType, setRegisteredType] = useState<SpaceEventType | null>(null);
  const [registeredOptionName, setRegisteredOptionName] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<SpaceEventAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [timerKey, setTimerKey] = useState(0);

  // Sin resultado de scan en memoria (recarga o link directo) no hay nada que mostrar.
  useEffect(() => {
    if (!scan) goToReservationsHome(history);
  }, [scan, history]);

  const result = useMemo(() => (scan ? buildReservationResult(scan) : null), [scan]);

  const handleAction = (action: SpaceEventAction) => {
    if (!scan || pendingAction) return;
    setPendingAction(action);
    setActionError(null);
    registerDecision(action, scan)
      .then((decided) => {
        setPendingAction(null);
        setRegisteredOptionName(decided.event?.optionName ?? null);
        setRegisteredType(decided.event?.type || RESERVATION_ACTION_EVENT_TYPE[action]);
      })
      .catch((err: Error) => {
        console.error(err);
        setPendingAction(null);
        setActionError(err.message);
      });
  };

  const handleScanNext = () => {
    clearLastSpaceScan();
    goToReservationScan(history);
  };

  const handleBackToStart = () => {
    clearLastSpaceScan();
    goToReservationsHome(history);
  };

  const restartCountdown = () => {
    setTimerKey((key) => key + 1);
  };

  useEffect(() => {
    if (!scan) return undefined;
    setSecondsLeft(30);
    const countdown = window.setInterval(() => {
      setSecondsLeft((seconds) => Math.max(seconds - 1, 0));
    }, 1000);
    const timeout = window.setTimeout(() => {
      clearLastSpaceScan();
      goToReservationsHome(history);
    }, 30000);
    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(timeout);
    };
  }, [scan, history, timerKey]);

  if (!result) return null;

  const modalResult = !registeredType
    ? null
    : registeredType === "alternative"
    ? alternativeActionResult(registeredOptionName, scan?.status === "alreadyUsed")
    : RESERVATION_EVENT_RESULTS[registeredType];

  return (
    <div className={classes.root} onPointerDown={restartCountdown}>
      <ReservationsHeader />
      <div className={classes.content}>
        <ReservationResultStatus
          variant={result.variant}
          title={result.title}
          description={result.description}
        />
        {result.guest && (
          <ReservationGuestCard
            name={result.guest.name}
            initials={result.guest.initials}
            franja={result.guest.franja}
            menu={result.guest.menu}
            primerIngreso={result.guest.primerIngreso}
          />
        )}
        {result.notice && result.variant !== "success" && (
          <ReservationResultNotice
            variant={result.variant}
            title={result.notice.title}
            description={result.notice.description}
          />
        )}
        {result.actions.length > 0 && (
          <div className={classes.manualActions}>
            {result.actions.map(({ action, label, icon }) => (
              <MuiButton
                key={action}
                className={action === "reject" ? classes.rejectButton : classes.allowButton}
                disableElevation
                disabled={pendingAction !== null}
                onClick={() => handleAction(action)}
              >
                {pendingAction === action ? (
                  <CircularProgress size={20} />
                ) : icon === "snack" ? (
                  <span className={classes.allowLabel}>
                    <FastfoodIcon className={classes.allowIcon} />
                    {label}
                  </span>
                ) : (
                  label
                )}
              </MuiButton>
            ))}
          </div>
        )}
        {actionError && <Typography className={classes.actionError}>{actionError}</Typography>}
        <div className={classes.autoBack} aria-live="polite">
          <span>Vuelve al inicio en</span>
          <span className={classes.autoBackTrack}>
            <span className={classes.autoBackProgress} style={{ width: `${(secondsLeft / 30) * 100}%` }} />
          </span>
          <strong>{`00:${secondsLeft.toString().padStart(2, "0")}`}</strong>
        </div>
        <Button size="large" className={classes.scanButton} onClick={handleScanNext}>
          <CropFreeIcon className={classes.scanIcon} />
          Escanear Siguiente
        </Button>
      </div>

      {modalResult && (
        <ReservationActionModal
          open
          variant={modalResult.variant}
          title={modalResult.title}
          description={modalResult.description}
          onBackToStart={handleBackToStart}
        />
      )}
    </div>
  );
};

export default ReservationResult;
